"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = SharedModal;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _useIsServerSideRender = _interopRequireDefault(require("@site/pattern-library-utils/hooks/useIsServerSideRender"));
var _reactDom = require("react-dom");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _core = require("@linaria/core");
var _button = _interopRequireDefault(require("@ux/button"));
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _icon = require("@site/pattern-library-utils/icon");
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _breakpoints = require("@site/pattern-library-utils/breakpoints");
var _motionFunctions = require("@site/pattern-library-core/motion-functions");
var _animation = require("../../../animation");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const ModalBackground = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "ModalBackground",
  class: "pl-m1yqzl7c"
});
const Modal = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "Modal",
  class: "pl-m929vxh"
});
const ButtonWrapper = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "ButtonWrapper",
  class: "pl-bzsieab"
});
const CloseButton = /*#__PURE__*/(0, _react2.styled)(_button.default)({
  name: "CloseButton",
  class: "pl-c1famn5u"
});
const ModalBody = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "ModalBody",
  class: "pl-m19ssb74"
});

/**
 * SharedModal component renders a modal dialog with customizable content
 *
 * @param {Object} props properties object
 * @param {string} props.id id of the modal
 * @param {string} props.dataCy data-cy attribute for testing
 * @param {boolean} props.show Whether the modal is visible
 * @param {string} props.modalCloseAriaLabel aria-label for the close button
 * @param {HTMLElement} props.portalElem element to which the modal is appended
 * @param {function} props.onClose function to call when the modal is closed
 * @param {string} props.modalHash hash to append to the URL when the modal is shown
 * @param {string} props.partial partial style to apply to the modal
 * @param {boolean} props.eager Whether the modal is eager to load
 * @param {React.ReactNode} props.children content to display inside the modal
 *
 * @returns {React.ReactPortal|null} Modal component or null if not visible.
 */
function SharedModal({
  id,
  dataCy = 'shared-modal-container',
  show = false,
  modalCloseAriaLabel = 'close-modal',
  portalElem,
  onClose,
  modalHash = 'module-hash',
  partial = 'light1',
  eager = false,
  className,
  children
}) {
  const innerRef = (0, _react.useRef)(null);
  const trackingProps = _patternLibraryUtils.tracking.props(modalHash, false, true);
  const isServerSideRender = (0, _useIsServerSideRender.default)();
  const getHashChain = () => {
    const hash = window.location.hash ? window.location.hash.substring(1) : '';
    return hash ? hash.split('--') : [];
  };
  (0, _react.useEffect)(() => {
    if (show) {
      const parts = getHashChain();
      if (parts[parts.length - 1] !== modalHash) {
        const newChain = parts.length ? parts.concat(modalHash).join('--') : modalHash;
        history.pushState(null, '', '#' + newChain);
      }
    }
  }, [show, modalHash]);
  const handleClose = e => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    onClose && onClose();
    const parts = getHashChain();
    if (parts.includes(modalHash)) {
      const newParts = parts.filter(part => part !== modalHash);
      if (newParts.length) {
        history.replaceState(null, '', '#' + newParts.join('--'));
      } else {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  };
  (0, _react.useEffect)(() => {
    if (!show) return;
    const handleHashChange = () => {
      const parts = getHashChain();
      if (parts[parts.length - 1] !== modalHash) {
        handleClose();
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [show, modalHash, onClose]);
  const handleEscape = () => {
    const parts = getHashChain();
    if (parts[parts.length - 1] === modalHash) {
      handleClose();
    }
  };
  (0, _patternLibraryUtils.useHeaderCartEvent)(_patternLibraryUtils.headerCartEvents.ADD_PENDING, handleEscape);
  (0, _patternLibraryUtils.useExitOnEscape)(handleEscape);
  const modal = /*#__PURE__*/_react.default.createElement(ModalBackground, (0, _extends2.default)({
    id: id,
    "data-cy": dataCy
  }, trackingProps, {
    "data-partial": partial,
    onClick: handleClose,
    className: (0, _core.cx)(className, eager && !show ? 'hidden' : 'visible')
  }), /*#__PURE__*/_react.default.createElement(Modal, {
    className: "modal-container",
    "data-cy": "modal-content",
    ref: innerRef,
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/_react.default.createElement(ButtonWrapper, null, /*#__PURE__*/_react.default.createElement(CloseButton, {
    "data-cy": "close",
    design: "stateless",
    "aria-label": modalCloseAriaLabel,
    icon: /*#__PURE__*/_react.default.createElement(_icon.Icon, {
      name: "x"
    }),
    onClick: handleClose
  })), /*#__PURE__*/_react.default.createElement(ModalBody, null, children)));
  if (!eager && !show) return null;
  return !isServerSideRender ? /*#__PURE__*/(0, _reactDom.createPortal)(modal, portalElem || document?.body) : modal;
}
SharedModal.propTypes = {
  id: _propTypes.default.string,
  dataCy: _propTypes.default.string,
  show: _propTypes.default.bool,
  modalCloseAriaLabel: _propTypes.default.string,
  portalElem: _propTypes.default.object,
  onClose: _propTypes.default.func,
  modalHash: _propTypes.default.string,
  children: _propTypes.default.node,
  partial: _propTypes.default.string,
  eager: _propTypes.default.bool,
  className: _propTypes.default.string
};